import { generateShapeSearchQuery } from '@takeshape/graphql';
import { getShapeById, type ProjectSchemaJSON, workflowsEnabled } from '@takeshape/schema';
import type { Sort } from 'app/common/types';
import { queryUser } from '../graphql-client.js';
import { FILTER_TYPES } from './constants.js';
import type { SearchFilter, SearchParams, SearchResponse, TaxonomySuggestResponse } from './types.js';

function filterByType(type: string) {
  return (filter: SearchFilter): boolean => filter.type === type;
}

export type SearchShapeParams = {
  projectSchema: ProjectSchemaJSON;
} & SearchParams;

export type SearchShapeQueryParams = {
  fields?: string[];
  from?: number;
  size?: number;
  sort?: Sort;
  terms?: string;
  filter?: unknown;
  where?: unknown;
  onlyEnabled?: boolean;
};

function formatQueryVars(params: SearchShapeParams): SearchShapeQueryParams {
  const { terms, sort, page, pageSize, filters, projectSchema } = params;
  const vars: SearchShapeQueryParams = {
    terms,
    sort,
    from: (page - 1) * pageSize,
    size: pageSize,
    onlyEnabled: false
  };

  const taxonomyTerms = filters
    .filter(filterByType(FILTER_TYPES.TAXONOMY))
    .map((f) => ({ term: { _references: f.value } }));
  const statusTerms = filters.filter(filterByType(FILTER_TYPES.STATUS)).map((f) => f.value);

  if (taxonomyTerms.length) {
    vars.filter = { bool: { must: taxonomyTerms } };
  }

  if (statusTerms.length && workflowsEnabled(projectSchema.apiVersion)) {
    vars.where = { _status: { in: statusTerms } };
  }

  return vars;
}

export async function searchShapeQuery(params: SearchShapeParams): Promise<{ search: SearchResponse }> {
  const {
    shapeIds: [shapeId],
    projectSchema,
    fields
  } = params;
  const shape = getShapeById(projectSchema, shapeId);
  const vars = formatQueryVars(params);

  if (!shape) {
    throw new Error(`Failed to create search query, missing shape with id "${shapeId}"`);
  }

  const query = generateShapeSearchQuery(projectSchema, shape, fields);
  return queryUser<{ search: SearchResponse }>(query)(vars);
}

export const SEARCH_QUERY = /* GraphQL */ `
  query search($terms: String, $shapeIds: [String]) {
    search: search(terms: $terms, shapeIds: $shapeIds) {
      total
      results {
        _id
        _shapeId
        searchSummary
      }
    }
  }
`;

export async function searchQuery({ terms, shapeIds }: Pick<SearchShapeParams, 'terms' | 'shapeIds'>): Promise<{
  search: SearchResponse;
}> {
  return queryUser<{ search: SearchResponse }>(SEARCH_QUERY)({
    terms,
    shapeIds
  });
}

export const SUGGEST_TAXONOMY_QUERY = /* GraphQL */ `
  query taxonomySuggest($terms: String, $shapeIds: [String]) {
    search: taxonomySuggest(terms: $terms, shapeIds: $shapeIds) {
      total
      items {
        _id
        _shapeId
        _shapeName
        text
      }
    }
  }
`;

export async function taxonomySuggestQuery({
  terms,
  shapeIds
}: Pick<SearchShapeParams, 'terms' | 'shapeIds'>): Promise<{
  search: TaxonomySuggestResponse;
}> {
  return queryUser<{ search: TaxonomySuggestResponse }>(SUGGEST_TAXONOMY_QUERY)({ terms, shapeIds });
}
